<template>
  <div class="flex px-[16vw]">
    <div class="sidebar_container"></div>
    <div class="flex flex-col grow w-full pt-1 proxima_nova">
      <SkeletonLoader
        v-if="isInitialLoading"
        :page="loaderPages.PERMISSION_MANAGEMENT"
      />
      <div v-else class="flex">
        <!-- Sidebar Navigation -->
        <div class="w-64">
          <div class="p-4">
            <BackButton @click="goBack" source="integrations" />
            <h2 class="text-lg text_color mt-2 mb-1">{{ roleName }}</h2>
            <p class="text-sm secondary_color">Manage Permissions</p>
          </div>
          <nav class="mt-4">
            <a
              v-for="(section, key) in sidebarSections"
              :key="key"
              @click="handleSectionClick(key, section)"
              class="flex items-center px-4 py-3 cursor-pointer sidebar_option"
              :class="{
                active: activeSection === key,
              }"
            >
              <component
                :is="section.icon"
                class="w-5 h-5 mr-3 secondary_color"
              />
              <span class="text-sm secondary_color">{{ section.name }}</span>
            </a>
          </nav>
        </div>

        <!-- Main Content -->
        <div class="flex-1 flex flex-col">
          <div class="p-6 pb-4">
            <div class="flex justify-end">
              <div class="flex gap-3">
                <a-button
                  class="primary_medium"
                  :loading="isSaving"
                  :disabled="!hasChanges || isSaving"
                  @click="handleSaveChanges"
                >
                  {{ isSaving ? "Saving..." : "Save changes" }}
                </a-button>
              </div>
            </div>
          </div>

          <div
            class="flex-1 overflow-y-auto custom_scrollbar px-6 pb-6 max-h-[calc(100vh-200px)]"
          >
            <div class="space-y-2">
              <SkeletonLoader
                v-if="isLoadingResources"
                :page="loaderPages.PERMISSION_SECTIONS"
              />
              <template v-else>
                <component
                  v-for="permission in currentSectionPermissions"
                  :key="permission.key"
                  :is="getPermissionComponent(permission)"
                  :permission="permission"
                  :model-value="selectedPermissions?.[permission?.key]"
                  :extra-values="extraPermissions?.[permission?.key]"
                  :disabled="isDefaultRole"
                  :abac-enabled="abacEnabled"
                  @update:model-value="
                    (value) => handleToggleChange(permission.key, value)
                  "
                  @update:extra="handleExtraUpdate"
                />
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import DashboardService from "@/src/api/DashboardService.js";
import CRMService from "@/src/api/CRMService.js";
import AuthPermissionService from "@/src/api/AuthPermissionService.js";
import eventBus from "@/src/utils/eventBus.js";
import {
  fetchPermissions,
  fetchABACPolicies,
} from "@/src/utils/permissionUtils.js";
import { isABACEnabled } from "@/src/utils/dynamicFormUtils.js";
import {
  SettingOutlined,
  SearchOutlined,
  PlayCircleOutlined,
  DatabaseOutlined,
  LineChartOutlined,
  ThunderboltOutlined,
  TeamOutlined,
  CodeOutlined,
  DollarOutlined,
  FileTextOutlined,
  ShoppingOutlined,
} from "@ant-design/icons-vue";
import BackButton from "../../components/BackButton.vue";
import {
  permissionConstants,
  getPermissionSectionsConfig,
  POLICY_SCOPES,
  POLICY_ACTIONS,
  NAMESPACE_CODEBASE,
  CODEBASE_ACTIONS,
} from "../../constants/permissionConstants.js";
import EmailPermission from "@/src/views/users/EmailPermission.vue";
import BasePermission from "@/src/views/users/BasePermission.vue";
import CRMPermission from "@/src/views/users/CRMPermission.vue";
import SimpleGranularPermission from "@/src/views/users/SimpleGranularPermission.vue";
import CodebasePermission from "@/src/views/users/CodebasePermission.vue";
import { featureNames } from "@/src/constants/globalConstants";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import {
  transformFromPolicies,
  getCRMResources,
  shouldEnableResource,
  getDefaultFullAccessPermissions,
  getDefaultEmptyPermissions,
  calculatePolicyDiff,
  getCodebaseActionFromPolicies,
} from "@/src/utils/policyUtils.js";

const route = useRoute();
const router = useRouter();
const store = useStore();
const roleName = ref("");
const isDefaultRole = ref(false);
const activeSection = ref("settings");
const dynamicCRMObjects = ref([]);
const isInitialLoading = ref(true);
const isLoadingResources = ref(false);
const isSaving = ref(false);
const dynamicNamespaces = ref([]);
const existingPolicies = ref([]);
const originalExtraPermissions = ref({});

const selectedPermissions = reactive({});
const extraPermissions = reactive({});

const originalPermissions = ref({});
const resourceIdMap = ref({});

const abacEnabled = computed(() => isABACEnabled());

const hasChanges = computed(() => {
  // Check if any permission toggles have changed
  const permissionsChanged = Object.keys(selectedPermissions).some((key) => {
    return selectedPermissions[key] !== originalPermissions.value[key];
  });

  if (permissionsChanged) {
    return true;
  }

  // Check if any extra permissions (CRM permissions, scopes, etc.) have changed
  const extraChanged = Object.keys(extraPermissions).some((key) => {
    const current = extraPermissions[key];
    const original = originalExtraPermissions.value[key];

    if (!current && !original) return false;
    if (!current || !original) return true;

    return JSON.stringify(current) !== JSON.stringify(original);
  });

  return extraChanged;
});

const permissionSectionsConfig = getPermissionSectionsConfig(featureNames);

const permissionSections = reactive(
  Object.fromEntries(
    Object.entries(permissionSectionsConfig).map(([key, section]) => {
      const iconComponent = {
        SettingOutlined,
        SearchOutlined,
        PlayCircleOutlined,
        DatabaseOutlined,
        LineChartOutlined,
        ThunderboltOutlined,
      }[section.iconName];

      return [
        key,
        {
          ...section,
          icon: iconComponent,
        },
      ];
    })
  )
);

const currentSectionPermissions = computed(() => {
  return sidebarSections.value[activeSection.value]?.permissions || [];
});

const sidebarSections = computed(() => {
  if (abacEnabled.value && dynamicNamespaces.value.length > 0) {
    const sections = {};
    dynamicNamespaces.value.forEach((namespace) => {
      const key = namespace.name.toLowerCase().replace(/\s+/g, "_");
      sections[key] = {
        id: namespace.id,
        name: namespace.name,
        icon: getIconForNamespace(namespace.name),
        permissions: namespace.permissions || [],
      };
    });
    return sections;
  }
  return permissionSections;
});

const getIconForNamespace = (name) => {
  const nameLower = name.toLowerCase();
  if (nameLower.includes("setting")) return SettingOutlined;
  if (nameLower.includes("prospect")) return SearchOutlined;
  if (nameLower.includes("engage")) return PlayCircleOutlined;
  if (nameLower.includes("crm")) return DatabaseOutlined;
  if (nameLower.includes("analytic")) return LineChartOutlined;
  if (nameLower.includes("automate")) return ThunderboltOutlined;
  if (nameLower.includes("hr")) return TeamOutlined;
  if (nameLower.includes("codebase")) return CodeOutlined;
  if (nameLower.includes("finance")) return DollarOutlined;
  if (nameLower.includes("invoice")) return FileTextOutlined;
  if (nameLower.includes("product")) return ShoppingOutlined;
  return SettingOutlined;
};

const getPermissionComponent = (permission) => {
  // Check for special extraInputs first (like emails)
  if (permission.extraInputs) {
    return EmailPermission;
  }

  // CODEBASE resources use a 3-level access selector (Read/Write/Manage)
  if (permission.namespace === NAMESPACE_CODEBASE) {
    return CodebasePermission;
  }

  // Check if it's a CRM namespace resource (except Lists)
  if (permission.namespace === "CRM" && permission.name !== "Lists") {
    return CRMPermission;
  }

  // HR-namespace resources derived from CRM objects (non-seeded) use the same matrix
  const isHRObjectResource =
    permission.namespace === "HR" &&
    !String(permission.key).startsWith("hr_");
  if (isHRObjectResource && permission.name !== "Lists") {
    return CRMPermission;
  }

  if (
    permission.hasExtra &&
    permission.namespace !== "CRM" &&
    !isHRObjectResource
  ) {
    return SimpleGranularPermission;
  }

  if (permission.hasExtra) {
    return CRMPermission;
  }

  // Legacy support for old constants-based approach
  switch (permission.key) {
    case permissionConstants.EMAILS:
    case "emails":
      return EmailPermission;
    case permissionConstants.CONTACTS:
    case permissionConstants.COMPANIES:
    case permissionConstants.LEADS:
    case permissionConstants.DEALS:
    case "Contacts":
    case "Companies":
    case "Leads":
    case "Deals":
    case "contacts":
    case "companies":
    case "leads":
    case "deals":
      return CRMPermission;
    case permissionConstants.LISTS:
    case "Lists":
    case "lists":
      return BasePermission;
    default:
      const isDynamicCRM = dynamicCRMObjects.value.some(
        (obj) => obj.slug === permission.key
      );
      return isDynamicCRM ? CRMPermission : BasePermission;
  }
};

const fetchDynamicCRMObjects = async () => {
  try {
    const response = await CRMService.fetchDynamicCRMListObjects();
    if (response.success && response.objects) {
      const excludedSlugs = ["tasks", "tickets"];
      const filteredObjects = response.objects.filter(
        (obj) => !excludedSlugs.includes(obj.slug)
      );

      dynamicCRMObjects.value = filteredObjects;

      const dynamicPermissions = filteredObjects.map((obj) => ({
        key: obj.slug,
        name: obj.plural_name || obj.name,
        description: `Access to view, edit and delete all ${(obj.plural_name || obj.name).toLowerCase()}`,
        hasExtra: true,
      }));

      const listsPermission = {
        key: permissionConstants.LISTS,
        name: "Lists",
        description:
          "Access to view, edit and delete all lists and setup private lists",
        hasExtra: true,
      };

      permissionSections.crm.permissions = [
        ...dynamicPermissions,
        listsPermission,
      ];
    }
  } catch (error) {
    console.error("Error fetching dynamic CRM objects:", error);
  }
};

const fetchNamespaces = async () => {
  try {
    const response = await AuthPermissionService.fetchNamespaces();
    if (response.data) {
      dynamicNamespaces.value = response.data
        .filter((item) => item.attributes?.enabled === true)
        .map((item) => ({
          id: item.attributes.id,
          name: item.attributes.name,
          permissions: [],
        }));

      // Fetch all resources in one API call
      await fetchAllResources();

      if (dynamicNamespaces.value.length > 0) {
        const firstKey = dynamicNamespaces.value[0].name
          .toLowerCase()
          .replace(/\s+/g, "_");
        activeSection.value = firstKey;
      }
    }
  } catch (error) {
    console.error("Error fetching namespaces:", error);
    message.error("Failed to fetch namespaces");
  }
};

const fetchNamespaceResources = async (namespaceId) => {
  try {
    isLoadingResources.value = true;
    const response =
      await AuthPermissionService.fetchNamespaceResources(namespaceId);

    if (response.data) {
      const filteredResources = response.data.filter(
        (item) =>
          !(item.id === "crm_tasks" && item.attributes?.is_system === true),
      );
      const transformedPermissions = filteredResources.map((item) => ({
        key: item.id,
        name: item.attributes.name,
        description:
          item.attributes.description || `Access to ${item.attributes.name}`,
        hasExtra: item.attributes.name !== "Lists",
      }));

      filteredResources.forEach((item) => {
        const resourceId = item.id;
        const resourceName = item.attributes.name;

        resourceIdMap.value[resourceId] = item.id;

        selectedPermissions[resourceId] = false;

        originalPermissions.value[resourceId] = false;
      });

      const namespace = dynamicNamespaces.value.find(
        (ns) => ns.id === namespaceId
      );
      if (namespace) {
        namespace.permissions = transformedPermissions;
      }
    }
  } catch (error) {
    console.error("Error fetching namespace resources:", error);
    message.error("Failed to fetch permissions");
  } finally {
    isLoadingResources.value = false;
  }
};

const fetchAllResources = async () => {
  try {
    isLoadingResources.value = true;
    const response = await AuthPermissionService.fetchAllResources();

    if (response.data) {
      // Hide the system `crm_tasks` resource — it duplicates the custom
      // "CRM Task" object surfaced via the dynamic CRM. Frontend-only filter.
      const filteredResources = response.data.filter(
        (item) =>
          !(item.id === "crm_tasks" && item.attributes?.is_system === true),
      );

      // Group resources by namespace
      const resourcesByNamespace = filteredResources.reduce((acc, item) => {
        const namespaceId = item.attributes.namespace_id;
        if (!acc[namespaceId]) {
          acc[namespaceId] = [];
        }
        acc[namespaceId].push(item);
        return acc;
      }, {});

      // Populate each namespace with its resources
      dynamicNamespaces.value.forEach((namespace) => {
        const resources = resourcesByNamespace[namespace.id] || [];

        const transformedPermissions = resources.map((item) => {
          const isCRM = item.attributes.namespace_id === "CRM";
          const isHRObjectResource =
            item.attributes.namespace_id === "HR" &&
            !String(item.id).startsWith("hr_");
          const permission = {
            key: item.id,
            name: item.attributes.name,
            description:
              item.attributes.description ||
              `Access to ${item.attributes.name}`,
            hasExtra:
              (isCRM || isHRObjectResource) &&
              item.attributes.name !== "Lists",
            namespace: item.attributes.namespace_id,
          };

          // Add special extraInputs for emails
          if (item.id === "emails") {
            permission.extraInputs = [
              {
                key: "emails_view",
                type: "select",
                label: "View",
                options: [
                  {
                    value: "team_emails",
                    label: "Emails from personal and shared mailboxes",
                  },
                  { value: "all_emails", label: "All Emails" },
                ],
                default: "team_emails",
              },
            ];
          }

          return permission;
        });

        resources.forEach((item) => {
          const resourceId = item.id;
          resourceIdMap.value[resourceId] = item.id;
          selectedPermissions[resourceId] = false;
          originalPermissions.value[resourceId] = false;
        });

        namespace.permissions = transformedPermissions;
      });
    }
  } catch (error) {
    console.error("Error fetching all resources:", error);
    message.error("Failed to fetch permissions");
  } finally {
    isLoadingResources.value = false;
  }
};

const handleSectionClick = async (key, section) => {
  activeSection.value = key;

  if (abacEnabled.value && section.id) {
    const namespace = dynamicNamespaces.value.find(
      (ns) => ns.id === section.id
    );

    if (
      namespace &&
      (!namespace.permissions || namespace.permissions.length === 0)
    ) {
      await fetchNamespaceResources(section.id);
    }
  }
};

const fetchRolePermissions = async () => {
  isLoadingResources.value = true;
  try {
    if (abacEnabled.value) {
      const response = await AuthPermissionService.fetchRole(
        route.params.role_id
      );
      if (response.data) {
        roleName.value = response.data.attributes?.name || "Unknown Role";
        isDefaultRole.value =
          response.data.attributes?.type === "default" || false;
      }

      try {
        const policiesResponse =
          await AuthPermissionService.fetchPoliciesByRole(route.params.role_id);

        if (policiesResponse.data && Array.isArray(policiesResponse.data)) {
          existingPolicies.value = policiesResponse.data;
        }
      } catch (policiesError) {
        console.error("Error fetching policies:", policiesError);
        message.error("Failed to load ABAC policies");
      }

      if (existingPolicies.value.length > 0) {
        const actionCountByResource = {};
        existingPolicies.value.forEach((p) => {
          const rid = p.attributes.resource_id;
          if (!actionCountByResource[rid]) actionCountByResource[rid] = new Set();
          actionCountByResource[rid].add(p.attributes.action);
        });

        dynamicNamespaces.value.forEach((namespace) => {
          namespace.permissions?.forEach((permission) => {
            const actions = actionCountByResource[permission.key];
            if (actions && actions.size > 1) {
              permission.hasExtra = true;
              if (permission.namespace === "CRM") {
                permission.granularActions = [...actions];
              } else {
                const standardActions = new Set(["ENABLED", "CREATE", "VIEW", "EDIT", "DELETE"]);
                actions.forEach((a) => standardActions.add(a));
                permission.granularActions = [...standardActions];
              }
            }
          });
        });

        const resourceTypeMap = new Map();
        dynamicNamespaces.value.forEach((namespace) => {
          namespace.permissions?.forEach((permission) => {
            resourceTypeMap.set(permission.key, {
              hasExtra: permission.hasExtra,
              namespace: permission.namespace,
              name: permission.name,
            });
          });
        });

        const crmResourceIds = new Set();
        const simpleResourceIds = new Set();
        const codebaseResourceIds = new Set();

        resourceTypeMap.forEach((info, resourceId) => {
          if (info.namespace === NAMESPACE_CODEBASE) {
            codebaseResourceIds.add(resourceId);
          } else if (info.hasExtra) {
            crmResourceIds.add(resourceId);
          } else {
            simpleResourceIds.add(resourceId);
          }
        });

        const allObjectProperties = {};
        crmResourceIds.forEach((resourceId) => {
          allObjectProperties[resourceId] =
            store.getters["objects/getAttributesBySlugOrId"](resourceId) || [];
        });

        const policies = existingPolicies.value.map((item) => ({
          resource: item.attributes.resource_id,
          action: item.attributes.action,
          scope: item.attributes.scope,
          condition: item.attributes.condition,
          role_id: item.attributes.role_id,
          workspace_id: item.attributes.workspace_id,
        }));

        const crmPolicies = policies.filter((p) =>
          crmResourceIds.has(p.resource)
        );

        const loadedPermissions = transformFromPolicies(
          crmPolicies,
          allObjectProperties
        );

        crmResourceIds.forEach((resourceId) => {
          const hasPolicy = shouldEnableResource(
            resourceId,
            existingPolicies.value
          );
          selectedPermissions[resourceId] = hasPolicy;
          originalPermissions.value[resourceId] = hasPolicy;

          if (loadedPermissions[resourceId]) {
            extraPermissions[resourceId] = loadedPermissions[resourceId];
          }
        });

        simpleResourceIds.forEach((resourceId) => {
          const hasPolicy = shouldEnableResource(
            resourceId,
            existingPolicies.value
          );
          selectedPermissions[resourceId] = hasPolicy;
          originalPermissions.value[resourceId] = hasPolicy;

          if (hasPolicy) {
            extraPermissions[resourceId] = {
              enabled: true,
              scope: POLICY_SCOPES.ALL,
            };
          }
        });

        codebaseResourceIds.forEach((resourceId) => {
          const codebaseAction = getCodebaseActionFromPolicies(
            resourceId,
            existingPolicies.value,
            route.params.role_id
          );
          const hasPolicy = !!codebaseAction;
          selectedPermissions[resourceId] = hasPolicy;
          originalPermissions.value[resourceId] = hasPolicy;

          if (hasPolicy) {
            extraPermissions[resourceId] = { codebaseAction };
          }
        });

        originalExtraPermissions.value = JSON.parse(
          JSON.stringify(extraPermissions)
        );
      }
    } else {
      const response = await DashboardService.fetchRolePermissions(
        route.params.role_id
      );
      if (response.success) {
        roleName.value = response.role.name;
        isDefaultRole.value = response.role.type === "default";

        const sectionsToProcess = Object.values(permissionSections);

        sectionsToProcess.forEach((section) => {
          section.permissions.forEach((permission) => {
            selectedPermissions[permission.key] =
              response.role.permissions.includes(permission.key);

            if (permission.hasExtra) {
              if (permission.extraInputs) {
                const defaultValues = permission.extraInputs.reduce(
                  (acc, input) => {
                    acc[input.key] = input.default;
                    return acc;
                  },
                  {}
                );

                const apiValues =
                  permission.key === "emails" && response.role.email_permissions
                    ? { emails_view: response.role.email_permissions }
                    : {};

                extraPermissions[permission.key] = {
                  ...defaultValues,
                  ...apiValues,
                };
              } else {
                const crmPermissions =
                  response.role.crm_permissions?.[permission.key];
                extraPermissions[permission.key] = {
                  create: crmPermissions?.create || true,
                  view: crmPermissions?.view || "none",
                  edit: crmPermissions?.edit || "none",
                  delete: crmPermissions?.delete || "conditional",
                  export: crmPermissions?.export || true,
                  import: crmPermissions?.import || true,
                  condition_type: crmPermissions?.condition_type || null,
                  assigned_users: crmPermissions?.assigned_users || [],
                  specific_users: crmPermissions?.specific_users || [],
                  specific_teams: crmPermissions?.specific_teams || [],
                };
              }
            }
          });
        });
      }
    }
  } catch (error) {
    console.error("Error fetching role permissions:", error);
    message.error("Failed to fetch role permissions");
  } finally {
    isLoadingResources.value = false;
    isInitialLoading.value = false;
  }
};

const handleSaveChanges = async () => {
  try {
    if (abacEnabled.value) {
      isSaving.value = true;

      try {
        // Build object properties map for ALL CRM resources (standard + custom)
        const allObjectProperties = {};

        Object.keys(extraPermissions).forEach((resourceId) => {
          const permissions = extraPermissions[resourceId];
          const isCRMStylePermission =
            permissions &&
            typeof permissions === "object" &&
            ("create" in permissions ||
              "view" in permissions ||
              "edit" in permissions ||
              "delete" in permissions);

          if (isCRMStylePermission) {
            // Try to get object properties by resource ID (slug or UUID)
            allObjectProperties[resourceId] =
              store.getters["objects/getAttributesBySlugOrId"](resourceId) ||
              [];
          }
        });

        const diff = calculatePolicyDiff(
          existingPolicies.value,
          extraPermissions,
          selectedPermissions,
          route.params.role_id,
          allObjectProperties
        );

        if (diff.skippedPolicies && diff.skippedPolicies.length > 0) {
          const skippedDetails = diff.skippedPolicies
            .map((s) => `${s.resource} (${s.action})`)
            .join(", ");
          message.warning(
            `Some policies were skipped due to incomplete configuration: ${skippedDetails}. Please configure condition type and properties for CONDITIONAL scopes.`,
            5
          );
        }

        let deletedCount = 0;
        let deleteFailed = [];
        if (diff.toDelete.length > 0) {
          const deleteResponse =
            await AuthPermissionService.batchDeletePolicies(diff.toDelete);
          deletedCount = deleteResponse.data.attributes.deleted_count;
          deleteFailed = deleteResponse.data.attributes.failed_ids || [];
        }

        let createdPolicies = [];
        if (diff.toCreate.length > 0) {
          const createResponse =
            await AuthPermissionService.batchCreatePolicies(diff.toCreate);
          createdPolicies = createResponse.data || [];
        }

        const updateResults = await Promise.allSettled(
          diff.toUpdate.map(({ id, policy }) =>
            AuthPermissionService.updatePolicy(id, policy)
          )
        );
        const updateFailed = updateResults.filter(
          (r) => r.status === "rejected"
        ).length;

        const deletedSet = new Set(
          diff.toDelete.filter((id) => !deleteFailed.includes(id))
        );
        existingPolicies.value = existingPolicies.value.filter(
          (p) => !deletedSet.has(p.id)
        );

        createdPolicies.forEach((policyResponse) => {
          existingPolicies.value.push(policyResponse);
        });

        updateResults.forEach((result, index) => {
          if (result.status === "fulfilled" && result.value?.data) {
            const policyId = diff.toUpdate[index].id;
            const idx = existingPolicies.value.findIndex(
              (p) => p.id === policyId
            );
            if (idx !== -1) {
              existingPolicies.value[idx] = result.value.data;
            }
          }
        });

        // Refresh permissions from backend to update Vuex store and localStorage
        // Only refresh if editing the current user's own role
        const isEditingOwnRole =
          String(store.state.rbac.currentRoleId) ===
          String(route.params.role_id);

        if (isEditingOwnRole) {
          await store.dispatch("rbac/fetchPermissions", { force: true });
        }

        const totalOperations =
          diff.toDelete.length + diff.toCreate.length + diff.toUpdate.length;
        const failedOperations =
          deleteFailed.length +
          (diff.toCreate.length - createdPolicies.length) +
          updateFailed;

        if (failedOperations > 0) {
          message.warning(
            `Updated successfully, but ${failedOperations} out of ${totalOperations} operations failed`
          );
        } else if (totalOperations > 0) {
          message.success("Permissions updated successfully");
        } else {
          message.info("No changes to save");
        }

        originalPermissions.value = { ...selectedPermissions };
        originalExtraPermissions.value = JSON.parse(
          JSON.stringify(extraPermissions)
        );

        eventBus.emit("permissions-updated");

        isSaving.value = false;
      } catch (error) {
        isSaving.value = false;
        throw error;
      }
    } else {
      const enabledPermissions = Object.entries(selectedPermissions)
        .filter(([_, value]) => value)
        .map(([key]) => key);

      const crmPermissions = {};
      const crmKeys = [
        ...dynamicCRMObjects.value.map((obj) => obj.slug),
        permissionConstants.LISTS,
      ];

      crmKeys.forEach((key) => {
        if (extraPermissions[key]) {
          crmPermissions[key] = {
            create: extraPermissions[key].create || false,
            view: extraPermissions[key].view || "none",
            edit: extraPermissions[key].edit || "none",
            delete: extraPermissions[key].delete || "conditional",
            export: extraPermissions[key].export || false,
            import: extraPermissions[key].import || false,
            condition_type: extraPermissions[key].condition_type || null,
            assigned_users: extraPermissions[key].assigned_users || [],
            specific_users: extraPermissions[key].specific_users || [],
            specific_teams: extraPermissions[key].specific_teams || [],
          };
        }
      });

      const apiData = {
        permissions: enabledPermissions,
        email_permissions:
          extraPermissions["emails"]?.emails_view || "team_emails",
        crm_permissions: crmPermissions,
      };

      const response = await DashboardService.updateRolePermissions(
        route.params.role_id,
        apiData
      );

      if (response.success) {
        message.success("Permissions updated successfully");
      } else {
        message.error("Failed to update permissions");
      }
    }
  } catch (error) {
    console.error("Error updating permissions:", error);
    message.error("An error occurred while updating permissions");
  }
};

const handleExtraUpdate = ({ key, inputKey, value }) => {
  if (!extraPermissions[key]) {
    extraPermissions[key] = {};
  }
  extraPermissions[key][inputKey] = value;
};

const handleToggleChange = (permissionKey, value) => {
  selectedPermissions[permissionKey] = value;

  if (abacEnabled.value) {
    const permission = currentSectionPermissions.value.find(
      (p) => p.key === permissionKey
    );
    const isCodebase = permission?.namespace === NAMESPACE_CODEBASE;
    const hasExtra = permission?.hasExtra || false;

    if (value) {
      // Check if we're restoring to the original state
      const wasOriginallyEnabled = originalPermissions.value[permissionKey];
      const hasOriginalExtra = originalExtraPermissions.value[permissionKey];

      if (wasOriginallyEnabled && hasOriginalExtra) {
        // Restore the original extraPermissions
        extraPermissions[permissionKey] = JSON.parse(
          JSON.stringify(originalExtraPermissions.value[permissionKey])
        );
      } else if (isCodebase) {
        // CODEBASE resources default to read-only access
        extraPermissions[permissionKey] = { codebaseAction: CODEBASE_ACTIONS.VIEW };
      } else {
        // Create new default permissions
        const hasExisting = shouldEnableResource(
          permissionKey,
          existingPolicies.value
        );
        if (!hasExisting) {
          if (hasExtra) {
            extraPermissions[permissionKey] = getDefaultFullAccessPermissions();
          } else {
            extraPermissions[permissionKey] = {
              enabled: true,
              scope: POLICY_SCOPES.ALL,
            };
          }
        }
      }
    } else {
      if (isCodebase) {
        extraPermissions[permissionKey] = { codebaseAction: null };
      } else if (hasExtra) {
        extraPermissions[permissionKey] = getDefaultEmptyPermissions();
      } else {
        extraPermissions[permissionKey] = {
          enabled: false,
          scope: POLICY_SCOPES.NONE,
        };
      }
    }
  }
};

const goBack = () => {
  router.push({
    path: "/settings",
    hash: "#users",
  });
  localStorage.setItem("settingsActiveKey", "2");
};

const handleCancel = () => {
  eventBus.emit(`permission-closed`);
};

onMounted(async () => {
  if (abacEnabled.value) {
    await fetchNamespaces();
    await store.dispatch("objects/fetchObjects");
    // Ensure users and teams are loaded before fetching role permissions
    await Promise.all([
      store.dispatch("users/fetchUsers"),
      store.dispatch("users/fetchTeams"),
    ]);
  } else {
    await fetchDynamicCRMObjects();
  }

  await fetchRolePermissions();
});
</script>

<style scoped>
.sidebar_option {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px;
  gap: 10px;
  align-self: stretch;
  border-radius: 8px;
  cursor: pointer;
  color: var(--secondary-color);
  font-family: "Proxima Nova", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  transition:
    background-color 0.2s ease-in-out,
    color 0.2s ease-in-out;
  width: 100%;
  line-height: 16px;
}

.sidebar_option:hover {
  background-color: var(--white-opacity-12);
}

.active {
  background-color: var(--white-opacity-12);
  color: var(--foreground-rgb);
}

.permission {
  border: 1px solid var(--white-opacity-8);
}
</style>
